import { createFileRoute, Link } from "@tanstack/react-router";
import { Page, SectionEyebrow, CTAButton } from "@/components/SiteLayout";
import hero from "@/assets/new_hero_sem_maya.mp4.asset.json";
import semVid from "@/assets/sem_bewegend.mp4.asset.json";
import mayaVid from "@/assets/maya_bewegend.mp4.asset.json";
import harunImg from "@/assets/harun.jpeg.asset.json";

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "Hi Coach — Schermpjes uit, energie aan" },
      { name: "description", content: "Welkom bij Hi Coach Club in Rhoon! Elke zondagochtend op beweegavontuur met Sem & Maya voor kinderen van 2 tot 6 jaar." },
      { property: "og:title", content: "Hi Coach Club Rhoon" },
      { property: "og:description", content: "Elke zondagochtend met Sem & Maya op beweegavontuur." },
      { property: "og:url", content: "/" },
    ],
    links: [{ rel: "canonical", href: "/" }],
  }),
  component: Home,
});

function Home() {
  return (
    <Page>
      {/* HERO */}
      <section className="relative h-[100svh] min-h-[640px] w-full overflow-hidden">
        <video
          className="absolute inset-0 h-full w-full object-cover object-center"
          src={hero.url}
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
        />
        <div className="absolute inset-0" style={{ background: "rgba(0,0,0,0.38)" }} />
        <div className="relative z-10 mx-auto max-w-7xl px-6 h-full flex flex-col justify-center items-center text-center md:items-start md:text-left">
          <span className="inline-block px-4 py-1 rounded-full bg-white/15 backdrop-blur text-white text-xs font-extrabold uppercase tracking-widest border border-white/30">
            Start september 2026
          </span>
          <h1 className="mt-6 text-white font-extrabold leading-[1.05] text-4xl sm:text-5xl md:text-6xl lg:text-7xl max-w-4xl">
            Schermpjes uit, energie aan en een leven lang plezier in bewegen!
          </h1>
          <p className="mt-5 text-white text-2xl md:text-3xl font-extrabold">
            Welkom bij Hi Coach Club in Rhoon!
          </p>
          <p className="mt-6 max-w-2xl text-white/90 text-lg md:text-xl">
            Elke zondagochtend met Sem &amp; Maya op beweegavontuur. Klimmen, springen, ontdekken en thuiskomen met een glimlach.
          </p>
          <div className="mt-8">
            <CTAButton />
          </div>
        </div>
      </section>

      {/* SECTIE 2: KEN JE DIT GEVOEL */}
      <section className="py-24 bg-white">
        <div className="mx-auto max-w-5xl px-6">
          <SectionEyebrow color="var(--rb-red)">Ken je dit gevoel?</SectionEyebrow>
          <p className="text-2xl md:text-3xl font-bold leading-snug max-w-3xl">
            Stel je eens voor… Het is zondagochtend. Geen strijd om de tablet. Geen kinderen die stilletjes op de bank verdwijnen in een scherm. Maar kinderen die rennen, springen, ontdekken en plezier maken.
          </p>
          <p className="mt-4 text-lg max-w-3xl">
            Kinderen die thuiskomen met rode wangetjes, een glimlach en vol verhalen over hun ochtend. Zou dat niet zijn wat we ieder kind gunnen?
          </p>
          <p className="mt-4 text-lg text-muted-foreground max-w-3xl">
            Toch zien we dat dit in de praktijk steeds minder vanzelfsprekend is. Schermen vragen veel aandacht en buitenspelen gebeurt minder spontaan. Daarnaast worden kinderen steeds jonger richting één vaste sport begeleid, terwijl vrij bewegen en ontdekken in de vroege jaren juist zo belangrijk zijn.
          </p>

          <h3 className="mt-12 text-2xl md:text-3xl font-extrabold">Introductie Hi Coach Club</h3>
          <p className="mt-3 text-lg max-w-3xl">
            Elke zondagochtend verandert de gymzaal in Rhoon in een interactieve wereld voor peuters en kleuters van 2 tot 6 jaar. Een alternatief voor de klassieke peutergym en kleutergym, maar dan met meer avontuur. Een kleinschalige, vertrouwde plek waar plezier in bewegen centraal staat.
          </p>

          <div className="grid md:grid-cols-3 gap-6 mt-10">
            {[
              { c: "#FF8080", t: "Geen vroege keuze voor één sport, maar direct een brede motorische basis voor elk kind." },
              { c: "#FFEB80", t: "Geen overvolle groepen, maar échte persoonlijke aandacht en ruimte om te groeien." },
              { c: "#8FC7FF", t: "Minder schermtijd en eindelijk weer lekker onbezorgd rennen, ontdekken en spelen." },
            ].map((k) => (
              <div key={k.t} className="rounded-2xl p-6 shadow-sm" style={{ background: k.c }}>
                <p className="font-bold text-lg" style={{ color: "#1a1a1a" }}>{k.t}</p>
              </div>
            ))}
          </div>

          <p className="mt-12 text-xl md:text-2xl font-extrabold">
            Kom rennen, kom springen, we roepen heel gauw: Hi Coach Club, we wachten op jou!
          </p>
        </div>
      </section>

      {/* SECTIE 3: DIT IS HI COACH */}
      <section className="py-24 gradient-rainbow-soft">
        <div className="mx-auto max-w-6xl px-6">
          <SectionEyebrow color="var(--rb-orange)">Dit is Hi Coach</SectionEyebrow>
          <p className="text-2xl md:text-3xl font-bold max-w-3xl leading-snug">
            Elke week verandert de gymzaal in een wereld vol avontuur. Geen vroege specialisatie, geen grote drukke klassen. Gewoon jouw kind, een kleine groep, en coaches die elk kind bij naam kennen.
          </p>

          <div className="grid md:grid-cols-3 gap-6 mt-12">
            {[
              { bg: "#FF8080", fg: "#1a1a1a", title: "Bewegen vanuit een brede basis", body: "Geen voetbal óf turnen. We trainen alle 10 de grondvormen van bewegen via de wetenschappelijke ASM-methode. De basis voor alles wat daarna komt." },
              { bg: "#FFEB80", fg: "#1a1a1a", title: "Leren via verhalen", body: "De fantasie van je kind staat centraal. Elke les heeft een spannend thema als rode draad waarin taal, creativiteit en motoriek samenkomen." },
              { bg: "#8FC7FF", fg: "#1a1a1a", title: "Kleine groepen", body: "Met een stabiele basis van 10 tot 12 kinderen krijgt iedereen échte aandacht. Onze coaches kennen elk kind bij naam en begeleiden ze op hun eigen niveau." },
            ].map((p) => (
              <div key={p.title} className="rounded-3xl p-8 shadow-lg" style={{ background: p.bg, color: p.fg }}>
                <h3 className="text-2xl font-extrabold">{p.title}</h3>
                <p className="mt-3 text-base/relaxed font-medium opacity-95">{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTIE 4: SEM EN MAYA */}
      <section className="py-24 bg-white">
        <div className="mx-auto max-w-6xl px-6 grid md:grid-cols-2 gap-12 items-center">
          <div>
            <SectionEyebrow color="var(--rb-violet)">Ontmoet Sem en Maya</SectionEyebrow>
            <h2 className="text-4xl md:text-5xl font-extrabold leading-tight">Twee helden. Elke zondag.</h2>
            <p className="mt-6 text-lg">
              Zij gidsen de kinderen over het parcours. Sem is de sportieve held met zijn flitsende sneakers en Maya is de stoere heldin met haar glanzende rode cape en gouden ster.
            </p>
            <p className="mt-4 text-lg">
              Voor de kleintjes zijn zij de vaste maatjes op wie ze elke week rekenen.
            </p>
            <p className="mt-4 text-lg font-extrabold">Niemand blijft aan de kant staan, iedereen doet mee!</p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {[
              { src: semVid.url, name: "Sem", color: "var(--rb-blue)" },
              { src: mayaVid.url, name: "Maya", color: "var(--rb-red)" },
            ].map((h) => (
              <div key={h.name} className="rounded-3xl overflow-hidden shadow-xl p-[4px] gradient-rainbow">
                <div className="rounded-[calc(1.5rem-4px)] overflow-hidden bg-white relative">
                  <video src={h.src} autoPlay muted loop playsInline className="w-full h-full object-cover aspect-[3/4]" />
                  <span className="absolute bottom-3 left-3 px-3 py-1 rounded-full text-white text-sm font-extrabold shadow-md" style={{ background: h.color }}>{h.name}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTIE 5: ZO ZIET DE LES ERUIT */}
      <section className="py-24" style={{ background: "linear-gradient(135deg, #EAF1FF, #F3E8FF)" }}>
        <div className="mx-auto max-w-6xl px-6">
          <SectionEyebrow color="var(--rb-blue)">Zo ziet de les eruit</SectionEyebrow>
          <p className="text-2xl md:text-3xl font-bold max-w-3xl leading-snug">
            Elke les heeft dezelfde vertrouwde opbouw. Voor de kinderen is die herkenbaarheid fijn, voor jou als ouder geeft het rust.
          </p>

          <div className="grid md:grid-cols-4 gap-6 mt-12">
            {[
              { c: "var(--rb-red)", n: 1, t: "De Ontvangst", d: "De coach begroet je kind op ooghoogte voor een high-five. Als ouder ben je altijd van harte welkom in de zaal." },
              { c: "var(--rb-orange)", n: 2, t: "De Startkring", d: "We beginnen rustig met een prentenboek. Sem en Maya luisteren mee. Het verhaal wordt de rode draad van de hele les." },
              { c: "var(--rb-green)", n: 3, t: "Het Parcours", d: "Tijd voor actie! Klimmen, springen, balanceren en rollen; de kinderen spelen het verhaal écht na. De uitdaging en parcoursen zijn specifiek afgestemd op de doelgroep: anders bij de peuters dan bij de kleuters." },
              { c: "var(--rb-blue)", n: 4, t: "De Eindkring", d: "We sluiten samen af en elk kind gaat naar huis met een lekker stuk vers fruit." },
            ].map((s) => (
              <div key={s.n} className="bg-white rounded-2xl p-6 shadow-md">
                <div className="h-14 w-14 rounded-full flex items-center justify-center text-white font-extrabold text-2xl shadow-lg" style={{ background: s.c }}>
                  {s.n}
                </div>
                <h3 className="mt-4 text-xl font-extrabold">{s.t}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{s.d}</p>
              </div>
            ))}
          </div>

          <p className="mt-12 text-xl font-extrabold">
            Elke les. Elke zondag. Dezelfde vertrouwde opbouw waar jouw kind op rekent.
          </p>
        </div>
      </section>

      {/* SECTIE 6: HARUN */}
      <section className="py-24 bg-white">
        <div className="mx-auto max-w-5xl px-6">
          <SectionEyebrow color="var(--rb-green)">Het gezicht achter de club</SectionEyebrow>
          <div className="mt-8 grid md:grid-cols-[auto_1fr] gap-10 items-start">
            <div className="relative mx-auto md:mx-0 shrink-0">
              <div className="absolute inset-0 translate-x-3 translate-y-3 rounded-3xl" style={{ background: "var(--rb-green)" }} />
              <img
                src={harunImg.url}
                alt="Harun, oprichter van Hi Coach Club"
                className="relative w-56 md:w-64 h-auto rounded-3xl border-4 border-white shadow-xl object-cover"
                loading="lazy"
              />
            </div>
            <div className="space-y-5 text-lg">
              <p>
                Ik ben Harun, mbo-docent, ik heb een achtergrond in de hulpverlening en ben assistent-voetbaltrainer bij SV Poortugaal. Samen met mijn vrouw Gülsüm, die al 12 jaar werkt in het basisonderwijs, zijn wij de trotse ouders van twee kinderen.
              </p>
              <p>
                Vanuit die achtergronden valt het ons op dat motorische basisvaardigheden bij de jeugd niet altijd meer vanzelfsprekend ontwikkeld zijn. Hoe kan dat, en wanneer begint dat eigenlijk?
              </p>
              <p>
                Thuis zoeken Gülsüm en ik dagelijks naar de balans tussen schermen, bewegen en een gezonde ontwikkeling. Die vraag hebben we niet weggestopt; we hebben er de Hi Coach Club van gemaakt.
              </p>
              <p>
                Hi Coach is geen gewoon sportclubje. Het is een sociaal initiatief, ontstaan vanuit het oprechte geloof dat kinderen van 2 tot 6 jaar een betere basis verdienen. Een basis die verder gaat dan sporten alleen, want kinderen groeien ook lichamelijk, sociaal en emotioneel. Krijgen ze daar in de huidige tijd wel genoeg ruimte voor?
              </p>
              <p>
                Onze droom is een generatie kinderen die met gezonde energie, plezier en vertrouwen opgroeit. Maar dit lukt ons niet zonder jullie. Elke ouder die zijn of haar kind aanmeldt bij Hi Coach, bouwt mee aan het antwoord op die vraag.
              </p>
            </div>
          </div>
          <p className="mt-10 text-2xl font-extrabold">
            Dat is wat ons drijft, elke zondagochtend opnieuw.
          </p>
        </div>
      </section>

      {/* SECTIE 7: QUOTES */}
      <section className="py-24 gradient-rainbow-soft">
        <div className="mx-auto max-w-5xl px-6">
          <h2 className="text-3xl md:text-4xl font-extrabold mb-10 text-center">Wat ouders zeggen:</h2>
          <div className="grid md:grid-cols-2 gap-10">
            {[
              "Heel interessant en het ziet er veelbelovend uit.",
              "Ik ben benieuwd naar de proefles!",
            ].map((q) => (
              <div key={q} className="relative bg-white rounded-3xl p-8 shadow-lg">
                <p className="text-2xl font-bold italic">"{q}"</p>
                {/* speech bubble tail */}
                <div
                  className="absolute -bottom-4 left-10 w-6 h-6 bg-white rotate-45 shadow-lg"
                  style={{ clipPath: "polygon(0 0, 100% 100%, 0 100%)" }}
                />
              </div>
            ))}
          </div>
          <p className="mt-14 text-center text-xl font-extrabold">
            Vanaf september 2026 gaat Hi Coach van start! Meld je kind snel aan, want er is slechts een beperkt aantal plekken voor de proeflessen beschikbaar per groep.
          </p>
        </div>
      </section>

      {/* SECTIE 8: PAKKETTEN */}
      <section className="py-24 bg-white">
        <div className="mx-auto max-w-6xl px-6">
          <SectionEyebrow color="var(--rb-green)">Kies jouw startpunt</SectionEyebrow>

          <div className="rounded-3xl p-8 md:p-10 mb-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6" style={{ background: "var(--rb-green)", color: "white" }}>
            <div>
              <h3 className="text-3xl font-extrabold">Gratis proefles</h3>
              <p className="mt-2 max-w-xl">Kom eerst kennismaken. Geen verplichting, geen kosten. Gewoon kijken of het klikt.</p>
            </div>
            <Link to="/inschrijven" className="btn-cta-inverse">Vraag gratis proefles aan</Link>
          </div>

          <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-6">
            {[
              { name: "Hi Instap", desc: "1 lessenreeks van 1 thema (7 lessen)", total: "€ 100,-", termijn: "2 maandelijkse termijnen (sept & okt)", month: "€ 50,-", color: "#FF8080" },
              { name: "Hi Sprong", desc: "Pakket van 2 thema's (14 lessen)", total: "€ 180,-", termijn: "4 maandelijkse termijnen (sept t/m dec)", month: "€ 45,-", color: "#FFEB80" },
              { name: "Hi Groei", desc: "Pakket van 3 thema's (21 lessen)", total: "€ 255,-", termijn: "6 maandelijkse termijnen (sept t/m feb)", month: "€ 42,50", color: "#FFB87F", badge: "Aanbevolen" },
              { name: "Hi Held", desc: "De ultieme sportreis: alle 5 de thema's (35 lessen) inclusief het grote diplomafeest.", total: "€ 400,-", termijn: "10 maandelijkse termijnen (sept t/m juni)", month: "€ 40,00", color: "#8FC7FF" },
            ].map((p) => (
              <div key={p.name} className="relative rounded-3xl bg-white border border-border shadow-md p-7 border-t-8 flex flex-col" style={{ borderTopColor: p.color, color: "#1a1a1a" }}>
                {p.badge && (
                  <span className="absolute -top-3 right-6 bg-foreground text-white text-xs font-extrabold uppercase tracking-wider px-3 py-1 rounded-full">{p.badge}</span>
                )}
                <h3 className="text-2xl font-extrabold">{p.name}</h3>
                <p className="mt-3 text-sm">{p.desc}</p>
                <p className="mt-4 text-sm"><span className="font-extrabold">Totaal:</span> {p.total}</p>
                <p className="mt-1 text-sm">{p.termijn}</p>
                <p className="mt-4 text-3xl font-extrabold">{p.month}<span className="text-sm font-bold"> /mnd</span></p>
              </div>
            ))}
          </div>
          <p className="mt-6 text-sm text-muted-foreground">
            Alle pakketten zijn exclusief het Hi Coach sportsetje, aan te schaffen bij inschrijving.
          </p>
        </div>
      </section>

      {/* SECTIE 9: URGENTIE */}
      <section className="py-24 gradient-rainbow-soft">
        <div className="mx-auto max-w-4xl px-6 text-center">
          <span className="uppercase tracking-widest text-xs font-extrabold bg-white px-4 py-1 rounded-full shadow-sm">Wees er op tijd bij</span>
          <h2 className="mt-6 text-4xl md:text-5xl font-extrabold">Vol is vol.</h2>
          <p className="mt-6 text-lg md:text-xl">
            Hi Coach start in september 2026 in Rhoon. De groepen worden klein gehouden, maximaal 12 kinderen per groep.
          </p>
          <p className="mt-6 text-xl font-extrabold">Geen kunstmatige druk. Gewoon een eerlijk gegeven.</p>
        </div>
      </section>

      {/* SECTIE 10: FINALE */}
      <section className="py-24 bg-white">
        <div className="mx-auto max-w-3xl px-6 text-center">
          <SectionEyebrow color="var(--rb-green)">Klaar om te starten?</SectionEyebrow>
          <h2 className="text-4xl md:text-5xl font-extrabold">Kom kennismaken.</h2>
          <p className="mt-4 text-lg text-muted-foreground">
            Kom een zondagochtend kennismaken en ervaar zelf wat Hi Coach doet met jouw kind.
          </p>
          <div className="mt-8 flex justify-center"><CTAButton /></div>
          <p className="mt-4 text-sm text-muted-foreground">
            Liever eerst even bellen of appen? Dat kan via <a className="underline" href="https://wa.me/31626716754">WhatsApp</a>.
          </p>
        </div>
      </section>
    </Page>
  );
}
